import { useMemo } from 'react';

import dayjs, { type Dayjs } from 'dayjs';
import { useParams } from 'react-router';

import type { ParsedPersonnel } from '@/apps/engagement-letter-database/hooks/useParseRecords';
import { useGetWaiverRecordQuery } from '@/gql/operations';
import { generateUserImageUrl } from '@/shared/helpers/userImagesUrlUtils';

type PersonnelInput =
  | {
      ActiveDirectoryLogin?: string | null;
      NamePreferredFull?: string | null;
      Email?: string | null;
    }
  | null
  | undefined;

const parsePersonnel = (user: PersonnelInput): ParsedPersonnel | undefined => {
  if (!user) return undefined;

  return {
    adSystemId: user.ActiveDirectoryLogin || undefined,
    displayName: user.NamePreferredFull || undefined,
    email: user.Email || undefined,
    imageUrlSmall: user.Email ? generateUserImageUrl(user.Email, 'M') : undefined,
  };
};

export interface ParsedWaiverRecord {
  id: string;
  clientName: string;
  matterNumber: string;
  clientNumber: string;
  clientContact: string;
  clientLetterDate: string;
  clientAttorney: string;
  adverseClientName: string;
  adverseContactSigner: string;
  adverseLetterDate: string;
  adverseAttorney: string;
  multipleWaivers: string;
  comments: string;
  createdAt: Dayjs | null;
  createdBy: string;
  createdByUser?: ParsedPersonnel;
  updatedAt: Dayjs | null;
  updatedBy: string;
  updatedByUser?: ParsedPersonnel;
}

/**
 * Fetches a single waiver record based on the :waiverId URL param.
 */
export const useWaiverRecord = () => {
  const { waiverId } = useParams<{ waiverId: string }>();

  const { data, loading, error } = useGetWaiverRecordQuery({
    variables: { waiver_id: waiverId! },
    skip: !waiverId,
  });

  const record: ParsedWaiverRecord | null = useMemo(() => {
    const found = data?.clm_waiver_records_by_pk;
    if (!found) return null;

    return {
      id: String(found.id),
      clientName: found.mlfsl_client?.ClientName1 ?? '',
      matterNumber: found.matter_number ?? '',
      clientNumber: found.client_number ?? '',
      clientContact: found.client_contact ?? '',
      clientLetterDate: found.client_letter_date ?? '',
      clientAttorney: found.mlb_attorney_on_client_letter_name ?? 'N/A',
      adverseClientName: found.adverse_client_name ?? '',
      adverseContactSigner: found.adverse_contact_signer ?? '',
      adverseLetterDate: found.adverse_letter_date ?? '',
      adverseAttorney: found.adverse_mlb_attorney_on_letter_name ?? 'N/A',
      multipleWaivers: found.multiple_waivers?.trim().toLowerCase() === 'yes' ? 'Yes' : 'No',
      comments: found.comments ?? '',
      createdAt: found.created_at ? dayjs(found.created_at) : null,
      createdBy: found.created_by_user?.ActiveDirectoryLogin || '',
      createdByUser: parsePersonnel(found.created_by_user),
      updatedAt: found.updated_at ? dayjs(found.updated_at) : null,
      updatedBy: found.updated_by_user?.ActiveDirectoryLogin || '',
      updatedByUser: parsePersonnel(found.updated_by_user),
    };
  }, [data]);

  return {
    record,
    isLoading: loading,
    error: error || null,
  };
};
